import React, { useState, useEffect } from 'react'
import Dropdown from '../../../components/common/Dropdown'
import Modal from '../../../components/common/Modal/Modal'
import ValidationBanner from '../../../components/common/ValidationBanner'
import ButtonGroup from './ButtonGroup'
import HoldValidationMessage from './HoldValidationMessage'
import { canHoldRequest } from './holdValidation'
import { APPLY_REQUEST_STATUS_HEADING } from './radioOptions'
import type { DropdownOption } from '../../../components/common/Dropdown'
import { ButtonStateConfig } from './buttonStateUtils'
import { denialReasonOptions } from './DenialReasonOptions'
import StatusRadioOptions from './StatusRadioOptions'
import { isISODateInPast } from '../../../utils/date'

interface DeniedDetails {
  deniedDate: string
  approverName: string
  denialReason: string
  approverComments: string
}

interface DeniedTimeOffFormProps {
  onSubmit?: (data: {
    status: string
    denialReason?: string
    otherDenialReason?: string
    comments: string
  }) => void
  onCancel?: () => void
  onStatusChange?: (status: string) => void
  isDenied?: boolean
  denialDetails?: DeniedDetails
  isRetracted?: boolean
  numberOfDates?: number
  startDate?: string
  activeForm?: 'approved' | 'denied' | 'hold' | 'partiallyapproved' | 'returned'
  requestId?: string
  isDisabledDueToDate?: boolean
  buttonStateConfig?: ButtonStateConfig
  enableOverrideSubmitButton?: boolean
  disableOverrideSubmitButton?: boolean
  isReturnedAllowed?: boolean
  disableEndDate?: string
  dateRequested?: string
  toTypeCd?: string
}

const DeniedTimeOffForm: React.FC<DeniedTimeOffFormProps> = ({
  onSubmit,
  onCancel,
  onStatusChange,
  isDenied = false,
  denialDetails,
  isRetracted = false,
  numberOfDates = 1,
  startDate = '',
  activeForm = 'approved',
  requestId = '',
  isDisabledDueToDate = false,
  buttonStateConfig,
  enableOverrideSubmitButton: _enableOverrideSubmitButton = false,
  disableOverrideSubmitButton = false,
  isReturnedAllowed = false,
  disableEndDate,
  dateRequested,
  toTypeCd,
}) => {
  // Calculate if Hold can be applied based on requested start date
  const canHold = canHoldRequest(dateRequested || startDate, toTypeCd)

  const [approverComments, setApproverComments] = useState('')
  const [selectedStatus, setSelectedStatus] = useState(
    isDenied ? 'denied' : 'approved'
  )
  const [selectedDenialReason, setSelectedDenialReason] = useState<
    DropdownOption | undefined
  >(
    isDenied && denialDetails
      ? denialReasonOptions.find(
          (opt) => opt.label === denialDetails.denialReason
        )
      : undefined
  )
  const [otherDenialReason, setOtherDenialReason] = useState('')
  const [showOverrideConfirmation, setShowOverrideConfirmation] =
    useState(false)
  const [validationError, setValidationError] = useState<string | null>(null)

  // Sync selectedStatus with activeForm when it changes
  useEffect(() => {
    if (activeForm === 'denied') {
      setSelectedStatus('denied')
    }
  }, [activeForm])
  
  const isPastDate = (date?: string) => {
    return isISODateInPast(date)
  }

  // Disable when BOTH start and end dates are before today — retractions/partial
  // retractions must remain processable regardless of past dates, so they're exempt.
  const areBothDatesInPast =
    !isRetracted && isPastDate(dateRequested) && isPastDate(disableEndDate)

  useEffect(() => {
    // Don't override when user explicitly navigated to the denied form
    if (activeForm === 'denied') return
    if (buttonStateConfig?.returnedChecked) {
      setSelectedStatus('returned')
    } else if (buttonStateConfig?.holdChecked) {
      setSelectedStatus('hold')
    } else if (buttonStateConfig?.deniedChecked) {
      setSelectedStatus('denied')
    } else if (buttonStateConfig?.approvedChecked) {
      setSelectedStatus('approved')
    }
  }, [buttonStateConfig, activeForm])

  const handleStatusChange = (status: string) => {
    setSelectedStatus(status)
    if (status !== 'denied') {
      setSelectedDenialReason(undefined)
    }
    if (onStatusChange) {
      onStatusChange(status)
    }
  }

  const handleSubmit = () => {
    // Validate that denial reason is selected if status is denied
    if (selectedStatus === 'denied' && !selectedDenialReason) {
      setValidationError('reasonNotSelected')
      return
    }

    // Validate that other denial reason is filled if 'other' is selected
    if (
      selectedStatus === 'denied' &&
      selectedDenialReason?.value === 'other' &&
      !otherDenialReason.trim()
    ) {
      setValidationError('otherReasonNotFilled')
      return
    }

    // Clear validation error if all checks pass
    setValidationError(null)

    // Show override confirmation when the approver is overriding a denied request
    if (isDenied && selectedStatus !== 'denied') {
      setShowOverrideConfirmation(true)
      return
    }

    // Otherwise proceed with normal submission
    onSubmit?.({
      status: selectedStatus,
      denialReason:
        selectedDenialReason?.value === 'other'
          ? otherDenialReason
          : selectedDenialReason?.label,
      comments: approverComments,
    })
  }

  /**
   * The override-unavailable message string used in two places: setting the error
   * and deriving whether comments/submit should be locked. Kept as a local const
   * so both references stay in sync.
   */
  const OVERRIDE_UNAVAILABLE_ERROR =
    'Override to approved not available due to existing full or 3 partial day request'

  /**
   * True when the override-unavailable banner is currently displayed.
   * Used to disable Comments and Submit while keeping Cancel enabled.
   * Covers three paths:
   * 1. disableOverrideSubmitButton prop — retraction-denied flow
   * 2. validationError set after modal confirmation — mock/test path
   * 3. isDenied + approvedDisabled — production DENIED + no-override-permission path
   *    (getDeniedFullApprovalConfig sets approvedDisabled:true, showOverrideForm stays false)
   */
  const isOverrideUnavailable =
    disableOverrideSubmitButton ||
    validationError === OVERRIDE_UNAVAILABLE_ERROR ||
    (isDenied && (buttonStateConfig?.approvedDisabled ?? false))

  const handleOverrideConfirmed = () => {
    if (requestId === 'appr-009') {
      setValidationError(OVERRIDE_UNAVAILABLE_ERROR)
      setShowOverrideConfirmation(false)
      return
    }

    setShowOverrideConfirmation(false)

    onSubmit?.({
      status: selectedStatus,
      comments: approverComments,
    })

    if (onStatusChange) {
      onStatusChange(selectedStatus)
    }
  }

  const getValidationBannerHeading = () => {
    if (
      validationError === OVERRIDE_UNAVAILABLE_ERROR
    ) {
      return (
        <div className="!pb-0 !p1 -medium [font-size:20px]">
          {validationError}
        </div>
      )
    }
    if (validationError === 'reasonNotSelected') {
      return (
        <div>
          <div className="!pt-6 !pb-0 !p1 -medium [font-size:20px]">
            Please select a Reason for denial
          </div>
          <div className="mt-2 ml-6">
            <span className="mr-2">
              Choose a{' '}
              <a
                href="#reason-for-denial"
                className="p3 -book !text-[#0074AD] !underline !pl-1"
              >
                Reason for Denial
              </a>{' '}
              from the list to continue.
            </span>
          </div>
        </div>
      )
    }
    if (validationError === 'otherReasonNotFilled') {
      return (
        <div>
          <div className="!pb-0 !p1 -medium [font-size:20px]">
            Reason for denial is required
          </div>
        </div>
      )
    }
    return null
  }

  return (
    <div className="contents">
      {validationError && (
        <div className="mb-6 px-[24px] pt-[24px] col-span-2">
          <ValidationBanner
            variant="error"
            heading={getValidationBannerHeading()}
          />
        </div>
      )}

  

      <div className="!px-[24px] !pt-0 !pb-[24px]">
        <p className="p4 -medium text-[#0D1C3D] !m-0 !pb-1">
          {APPLY_REQUEST_STATUS_HEADING.text}
        </p>

        {/* Status Radio Buttons */}
        <div className="space-y-4 mb-8 !pb-6">
          <StatusRadioOptions
            selectedStatus={selectedStatus}
            onStatusChange={handleStatusChange}
            buttonStateConfig={buttonStateConfig}
            canHold={canHold}
            isRetracted={isRetracted}
            numberOfDates={numberOfDates}
            isDenied={isDenied}
            isReturnedAllowed={isReturnedAllowed}
            disableApproved={disableOverrideSubmitButton}
            disableAllOptions={isDisabledDueToDate || areBothDatesInPast}
          />
        </div>

        {/* Hold Validation Message */}
        {!canHold && (
          <HoldValidationMessage
            message={
              toTypeCd === 'A'
                ? 'Cannot Hold Request: Start date is less than 1 day'
                : 'Cannot Hold Request: Start date is less than 15 days'
            }
          />
        )}

        {/* Reason for Denial Dropdown - Only show when Denied is selected and record is not already denied */}
        {selectedStatus === 'denied' && !isDenied && (
          <div className="mb-6 pt-6 !pb-6" id="reason-for-denial">
            <h5 className="text-[#0D1C3D] text-base font-light leading-6 tracking-[0.16px] mb-4">
              Reason for Denial<span className="text-red-600">*</span>
            </h5>
            <Dropdown
              options={denialReasonOptions}
              selectedValue={selectedDenialReason}
              placeholder="Select a Reason for Denial"
              onSelect={(option) => {
                setSelectedDenialReason(option)
                setValidationError(null)
              }}
              minWidth="270px"
            />
            {validationError === 'reasonNotSelected' && (
              <p className="text-red-600 text-xs mt-1 flex items-center gap-1">
                <img
                  src="/assets/images/errorCircleSmall.svg"
                  alt="error"
                  className="inline w-4 h-4"
                />
                <span>Reason for Denial is required</span>
              </p>
            )}
          </div>
        )}

        {/* Describe Other - Show only when "Other" is selected */}
        {selectedStatus === 'denied' &&
          !isDenied &&
          selectedDenialReason?.value === 'other' && (
            <div className="mb-6 pt-6 !pb-6">
              <label htmlFor="otherReason" className="block p3 -medium mb-1">
                Describe Other<span className="text-red-600">*</span>
              </label>
              <input
                type="text"
                id="otherReason"
                className="w-full h-10 border rounded p-2 disabled:bg-gray-100 disabled:cursor-not-allowed"
                maxLength={100}
                value={otherDenialReason}
                onChange={(e) => {
                  setOtherDenialReason(e.target.value)
                  setValidationError(null)
                }}
              />
              {validationError === 'otherReasonNotFilled' && (
                <p className="text-red-600 text-xs mt-1 flex items-center gap-1">
                  <img
                    src="/assets/images/errorCircleSmall.svg"
                    alt="error"
                    className="inline w-4 h-4"
                  />
                  <span>Reason for Denial is required</span>
                </p>
              )}
            </div>
          )}

        {/* Approver Comments */}
        <div className="mb-6 pt-6 !pb-6">
          <label htmlFor="comments" className="block p3 -medium mb-1">
            Approver Comments
          </label>
          {/*
           * Disable comments when Override to Approved is unavailable
           * (existing Full Day or 3 Partial Day request on the same date),
           * or when the request date has already passed and submission is
           * blocked — matches legacy application behavior: comments are
           * read-only when no action can be taken.
           */}
          <textarea
            id="comments"
            className="w-full border rounded p-2 min-h-20 disabled:bg-gray-100 disabled:cursor-not-allowed"
            maxLength={1000}
            value={approverComments}
            onChange={(e) => setApproverComments(e.target.value)}
            disabled={isOverrideUnavailable || isDisabledDueToDate || areBothDatesInPast}
          />
          <div className="text-left text-xs text-gray-500 mt-1">
            {1000 - approverComments.length} of 1000 characters left
          </div>
        </div>

        {/* Buttons - Always show, but disable when denied and status is still 'denied' */}
        <div className="!pb-8">
          {/*
           * Cancel must always remain enabled so users can navigate back to
           * the List View even when Override to Approved is unavailable.
           * Submit is disabled independently when no action can be taken
           * (e.g. status unchanged, past date, or override blocked).
           */}
          <ButtonGroup
            onCancel={onCancel}
            onSubmit={handleSubmit}
            cancelDisabled={false}
            submitDisabled={
              isOverrideUnavailable ||
              (isDenied && selectedStatus === 'denied') ||
              isDisabledDueToDate ||
              areBothDatesInPast
            }
          />
        </div>

        {/* Override Confirmation Modal */}
        <Modal
          isOpen={showOverrideConfirmation}
          title="Override Denied Request"
          onClose={() => setShowOverrideConfirmation(false)}
          clearBtn="Cancel"
          submitBtn="Yes, Override Denied Request"
          onSecondary={() => setShowOverrideConfirmation(false)}
          onPrimary={handleOverrideConfirmed}
          iconSrc="/assets/images/alertSolid.svg"
          iconAlt="Warning"
          containerClassName="!w-[503px] !min-w-[360px] !max-w-[928px] !p-6"
          buttonsClassName="!justify-end"
          primaryBtnClassName="!bg-[#0074AD] !text-white"
        >
          <p>Do you wish to override this denied request?</p>
        </Modal>
      </div>
    </div>
  )
}

export default DeniedTimeOffForm
